import { useSavedSearchStore } from '@/stores/filters';
import { walkthroughSetupIds } from '@/constants/featureAnnouncements';
import { onboardingWalkthroughSetupIds } from '@/constants/onboardingChecklist';

import useAcvDatadog from '@/composables/useAcvDatadog';
import { storeToRefs } from 'pinia';
/**
 * Opens whatever UI a walkthrough's steps need to be pointing at, before it starts.
 *
 * The Saved Searches walkthrough is the example: one of its steps highlights "Create a Saved
 * Search", a button that only exists while the sidebar is open. So that announcement declares
 * `setup: walkthroughSetupIds.openSavedSearchSidebar`, and the function below opens the sidebar
 * as the walkthrough launches. Without it the step would simply be skipped, because a
 * walkthrough only shows steps whose targets are visible when it starts.
 *
 * Announcements name a key rather than holding the function, so that
 * `constants/featureAnnouncements.js` stays plain data — no store imports, no import cycles,
 * still serializable if announcements ever come from an API.
 *
 * Nothing is undone when the walkthrough ends: the sidebar stays open. The carrier was just
 * shown it, so closing it again would take away what they were shown.
 */
const walkthroughSetups = {
  [walkthroughSetupIds.openSavedSearchSidebar]: () => {
    const savedSearchStore = useSavedSearchStore();
    const { isSavedSearchSideBarOpen } = storeToRefs(savedSearchStore);
    savedSearchStore.getSavedSearchesForCarrier();
    isSavedSearchSideBarOpen.value = true;
  },
  [onboardingWalkthroughSetupIds.openAccountMenu]: () => {
    // TODO: open dropdown menu
  },
  [onboardingWalkthroughSetupIds.openSavedSearchSidebar]: () => {
    // TODO: open saved search sidebar
  },
  [onboardingWalkthroughSetupIds.openCreateSavedSearch]: () => {
    // TODO: open create a new saved search
  },
  [onboardingWalkthroughSetupIds.closeAndOpenSavedSearch]: () => {
    // TODO: close form and reopen saved search sidebar
  },
  [onboardingWalkthroughSetupIds.findInstantBookableJob]: () => {
    // TODO: find instant bookable job
  },
  [onboardingWalkthroughSetupIds.openJobCard]: () => {
    // TODO: open the job card modal
  },
};

/**
 * Runs the named setup, if there is one. An unknown key is reported and otherwise ignored:
 * the walkthrough still runs, showing whichever steps happen to be visible, rather than
 * failing outright over stale content.
 */
export const runWalkthroughSetup = (setupId) => {
  if (!setupId) {
    return;
  }

  const setup = walkthroughSetups[setupId];

  if (!setup) {
    useAcvDatadog().addRumError('Unknown walkthrough setup id', { setupId });
    return;
  }

  setup();
};
